// S5 (v0.16.0) — Device profile manager.
// Lists tenant device profiles, lets admin create / edit / delete, and
// applies a profile to a multi-select of cameras.

import { useState } from "react";

import {
  useCreateDeviceProfile,
  useDeleteDeviceProfile,
  useDeviceProfiles,
  useUpdateDeviceProfile,
  type DeviceProfile,
  type DeviceProfileInput,
} from "@/api/s5";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMPTY_FORM: DeviceProfileInput = {
  name: "",
  description: "",
  recording_enabled: true,
  recording_mode: "continuous",
  retention_days: 30,
  analytics_enabled: false,
  snapshot_interval_minutes: 60,
  stream_quality: "sub",
};

export default function ProfileManagerPage() {
  const { data: profiles = [], isLoading } = useDeviceProfiles();
  const [editing, setEditing] = useState<DeviceProfile | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<DeviceProfileInput>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const create = useCreateDeviceProfile();
  const update = useUpdateDeviceProfile();
  const del = useDeleteDeviceProfile();

  function openCreate() {
    setForm(EMPTY_FORM);
    setEditing(null);
    setCreating(true);
    setError(null);
  }

  function openEdit(p: DeviceProfile) {
    setForm({
      name: p.name,
      description: p.description ?? "",
      recording_enabled: p.recording_enabled,
      recording_mode: p.recording_mode,
      retention_days: p.retention_days,
      analytics_enabled: p.analytics_enabled,
      snapshot_interval_minutes: p.snapshot_interval_minutes,
      stream_quality: p.stream_quality,
    });
    setEditing(p);
    setCreating(false);
    setError(null);
  }

  function closeForm() {
    setEditing(null);
    setCreating(false);
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, payload: form });
      } else {
        await create.mutateAsync(form);
      }
      closeForm();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Save failed");
    }
  }

  async function handleDelete(p: DeviceProfile) {
    if (!confirm(`Delete profile "${p.name}"?`)) return;
    try {
      await del.mutateAsync(p.id);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      alert(typeof detail === "string" ? detail : "Delete failed");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Device Profiles</h1>
          <p className="text-sm text-muted-foreground">
            Reusable recording / retention / analytics templates applied to
            cameras individually or in bulk.
          </p>
        </div>
        <Button onClick={openCreate}>Create Profile</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Loading…</p>
          ) : profiles.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              No profiles yet. Create your first one above.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Recording</th>
                  <th className="px-3 py-2">Retention</th>
                  <th className="px-3 py-2">Stream</th>
                  <th className="px-3 py-2">Cameras</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((p) => (
                  <tr key={p.id} className="border-t">
                    <td className="px-3 py-2 font-medium">{p.name}</td>
                    <td className="px-3 py-2 capitalize">{p.recording_mode}</td>
                    <td className="px-3 py-2">{p.retention_days} days</td>
                    <td className="px-3 py-2 capitalize">{p.stream_quality}</td>
                    <td className="px-3 py-2">{p.camera_count}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(p)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(p)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {(creating || editing) && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-lg font-semibold">
              {editing ? `Edit "${editing.name}"` : "New Profile"}
            </h2>
            <form className="space-y-3" onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Profile Name</Label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) =>
                      setForm({ ...form, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={form.description ?? ""}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Recording Mode</Label>
                  <select
                    className="w-full rounded border bg-background px-2 py-1 text-sm"
                    value={form.recording_mode ?? "continuous"}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        recording_mode:
                          e.target.value as DeviceProfileInput["recording_mode"],
                      })
                    }
                  >
                    <option value="continuous">Continuous</option>
                    <option value="motion">Motion</option>
                    <option value="schedule">Schedule</option>
                  </select>
                </div>
                <div>
                  <Label>Retention (days)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    value={form.retention_days ?? 30}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        retention_days: Number(e.target.value) || 30,
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Stream Quality</Label>
                  <select
                    className="w-full rounded border bg-background px-2 py-1 text-sm"
                    value={form.stream_quality ?? "sub"}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        stream_quality:
                          e.target.value as DeviceProfileInput["stream_quality"],
                      })
                    }
                  >
                    <option value="sub">Low quality</option>
                    <option value="main">High quality</option>
                  </select>
                </div>
                <div>
                  <Label>Snapshot Interval (min)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={1440}
                    value={form.snapshot_interval_minutes ?? 60}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        snapshot_interval_minutes: Number(e.target.value) || 60,
                      })
                    }
                  />
                </div>
                <label className="col-span-2 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.recording_enabled ?? true}
                    onChange={(e) =>
                      setForm({ ...form, recording_enabled: e.target.checked })
                    }
                  />
                  Recording enabled
                </label>
                <label className="col-span-2 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.analytics_enabled ?? false}
                    onChange={(e) =>
                      setForm({ ...form, analytics_enabled: e.target.checked })
                    }
                  />
                  Analytics enabled
                </label>
              </div>
              {error && (
                <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
                  {error}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={closeForm}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editing ? "Save Changes" : "Create Profile"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
